import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import List from './subcomponents/List'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Referral/PayoutProfiles/List/filters'
import PageHeader from 'components/PageHeader'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-payout-profiles.svg'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import GridSpinner from 'components/UI/GridSpinner'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'

const PayoutProfiles = () => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()

  const { data, loading, pagination, loaded } = useSelector(
    (state) => state['referral/payout-profiles'],
  )

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['referral/payout-profiles'].filters),
    config: config(),
    setAction: 'USER/PAYOUT_PROFILES_SET_FILTER',
    localStorageKey: 'referral/payout-profiles',
  })

  useEffect(() => {
    dispatch({
      type: 'USER/DROP_USER_PAYOUT_PROFILES',
    })
    setPage(1)
  }, [filterManager.filters])

  useEffect(() => {
    dispatch({
      type: 'REFERRAL/PAYOUT_PROFILES_FETCH',
      payload: {
        params: {
          ...filterManager.params,
          offset: (page - 1) * pagination.limit,
          limit: pagination.limit,
        },
      },
    })
  }, [filterManager.params, page])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  return (
    <>
      <Grid direction={'column'} className={'margin-bottom-40'} gap={24}>
        <PageHeader>{t('referral::navigation.payout-profiles')}</PageHeader>

        {firstLoad && <GridSpinner width={32} />}

        {!firstLoad && !totallyEmptyList && (
          <div>
            <Button
              component={Link}
              to={pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_NEW)}
              variant={'primary'}
              size={'l'}
              startIcon={<Icon name={'plus'} />}
            >
              {t('referral::payout_profiles.add')}
            </Button>
          </div>
        )}
      </Grid>

      {!firstLoad && totallyEmptyList && (
        <Grid justifyContent={'center'} gap={32}>
          <EmptyResults
            header={t('referral::payout_profiles.empty_list.header')}
            text={t('referral::payout_profiles.empty_list.text')}
            image={<EmptyResultsSVG />}
            actionButton={
              <Button
                component={Link}
                to={pathGen(ROUTES.REFERRAL_PAYOUT_PROFILE_NEW)}
                variant={'primary'}
                size={'l'}
                startIcon={<Icon name={'plus'} />}
                fullWidth={!breakpoints.sm}
                noWrap
              >
                {t('referral::payout_profiles.add')}
              </Button>
            }
          />
        </Grid>
      )}

      {!firstLoad && !totallyEmptyList && (
        <>
          <FilterPanel filterManager={filterManager} className={'margin-bottom-32'} />
          <List
            data={data}
            loading={loading}
            pagination={pagination}
            onChangePage={(val) => setPage(page + val)}
            EmptyComponent={() => (
              <EmptyResults
                header={t('referral::payout_profiles.empty_list.header')}
                text={t('referral::payout_profiles.empty_list.text', {
                  context: 'with_filters',
                })}
                image={<EmptyResultsSVG />}
              />
            )}
          />
        </>
      )}
    </>
  )
}

export default PayoutProfiles
